/**
 * LiveEventTailPage — /live
 *
 * US-24-GR-11 — Real-time scrolling event log fed by SignalR Admin_Orchestrator group.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminHub } from '../contexts/AdminHubContext';
import type { AdminOrchestratorEvent, EventType, Severity } from '../hooks/useAdminOrchestratorHub';

// ---------------------------------------------------------------------------
// Severity helpers
// ---------------------------------------------------------------------------

function severityDot(severity: Severity): string {
  switch (severity) {
    case 'error': return 'bg-red-500';
    case 'warn':  return 'bg-amber-400';
    default:      return 'bg-zinc-500';
  }
}

function eventTypeColor(type: EventType): string {
  switch (type) {
    case 'Failed':
    case 'DlqAlert':
    case 'ThresholdAlert':
      return 'text-red-400 bg-red-500/10 border-red-500/20';
    case 'Blocked':
    case 'ActionRequired':
      return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
    case 'Completed':
    case 'RunComplete':
      return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    default:
      return 'text-zinc-400 bg-zinc-800 border-zinc-700';
  }
}

// ---------------------------------------------------------------------------
// Rate badge — fires when > 50 events arrive in 1s
// ---------------------------------------------------------------------------

const ALL_EVENT_TYPES: EventType[] = [
  'Progress', 'Blocked', 'Completed', 'Failed',
  'ActionRequired', 'RunComplete', 'HealthCheck', 'DlqAlert', 'ThresholdAlert',
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LiveEventTailPage() {
  const { events, status, pause, resume } = useAdminHub();
  const navigate = useNavigate();

  const [isPaused, setIsPaused] = useState(false);
  const [filterEventTypes, setFilterEventTypes] = useState<Set<EventType>>(new Set());
  const [filterHasError, setFilterHasError] = useState(false);
  const [filterProfileId, setFilterProfileId] = useState('');
  const [filterJobId, setFilterJobId] = useState('');
  const [eventsPerSec, setEventsPerSec] = useState(0);

  // Rate detection
  const eventCountRef = useRef(0);
  const prevEventsLen = useRef(0);

  useEffect(() => {
    const diff = events.length - prevEventsLen.current;
    if (diff > 0) {
      eventCountRef.current += diff;
    }
    prevEventsLen.current = events.length;
  }, [events]);

  useEffect(() => {
    const interval = setInterval(() => {
      setEventsPerSec(eventCountRef.current);
      eventCountRef.current = 0;
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  function handlePauseResume() {
    if (isPaused) {
      resume();
      setIsPaused(false);
    } else {
      pause();
      setIsPaused(true);
    }
  }

  function toggleEventType(type: EventType) {
    setFilterEventTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function clearFilters() {
    setFilterEventTypes(new Set());
    setFilterHasError(false);
    setFilterProfileId('');
    setFilterJobId('');
  }

  const hasAnyFilter =
    filterEventTypes.size > 0 || filterHasError || filterProfileId || filterJobId;

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (filterEventTypes.size > 0 && !filterEventTypes.has(e.EventType)) return false;
      if (filterHasError && e.Severity !== 'error') return false;
      if (filterProfileId && e.ProfileId !== filterProfileId) return false;
      if (filterJobId && e.JobId !== filterJobId) return false;
      return true;
    });
  }, [events, filterEventTypes, filterHasError, filterProfileId, filterJobId]);

  function formatTimestamp(ts: string): string {
    try {
      return new Date(ts).toLocaleTimeString();
    } catch {
      return ts;
    }
  }

  function truncateId(id: string | null): string {
    if (!id) return '—';
    return id.slice(0, 8) + '…';
  }

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Page header bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-800 bg-zinc-950">
        <div className="flex items-center gap-3">
          <h1 className="text-sm font-semibold text-zinc-100">Live Event Tail</h1>
          {eventsPerSec > 50 && (
            <span
              aria-live="polite"
              className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-medium border border-amber-500/30"
            >
              {eventsPerSec}/s
            </span>
          )}
          {isPaused && (
            <span
              aria-live="polite"
              className="px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300 text-xs font-medium"
            >
              Paused
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handlePauseResume}
          aria-pressed={isPaused}
          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors min-h-[48px]"
        >
          {isPaused ? 'Resume' : 'Pause'}
        </button>
      </div>

      {/* Reconnect banner */}
      {status === 'Connecting' && !isPaused && (
        <div
          aria-live="polite"
          role="status"
          className="px-6 py-2 bg-amber-500/10 border-b border-amber-500/20 text-amber-300 text-xs"
        >
          Reconnecting to event stream…
        </div>
      )}
      {status === 'Disconnected' && (
        <div
          aria-live="polite"
          role="status"
          className="px-6 py-2 bg-red-500/10 border-b border-red-500/20 text-red-300 text-xs"
        >
          Disconnected from event stream. Attempting to reconnect…
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-zinc-800 bg-zinc-950">
        {ALL_EVENT_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => toggleEventType(type)}
            aria-pressed={filterEventTypes.has(type)}
            className={[
              'px-2 py-1 rounded-full text-xs font-medium border transition-colors min-h-[48px]',
              filterEventTypes.has(type)
                ? 'bg-violet-600 text-zinc-950 border-violet-500'
                : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700',
            ].join(' ')}
          >
            {type}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setFilterHasError((v) => !v)}
          aria-pressed={filterHasError}
          className={[
            'px-2 py-1 rounded-full text-xs font-medium border transition-colors min-h-[48px]',
            filterHasError
              ? 'bg-red-600 text-white border-red-500'
              : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700',
          ].join(' ')}
        >
          has-error
        </button>

        {filterProfileId && (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-violet-900/40 text-violet-300 border border-violet-500/30">
            profileId: {truncateId(filterProfileId)}
            <button
              type="button"
              aria-label="Clear profileId filter"
              onClick={() => setFilterProfileId('')}
              className="ml-1 text-violet-400 hover:text-white"
            >
              ×
            </button>
          </span>
        )}

        {filterJobId && (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-violet-900/40 text-violet-300 border border-violet-500/30">
            jobId: {truncateId(filterJobId)}
            <button
              type="button"
              aria-label="Clear jobId filter"
              onClick={() => setFilterJobId('')}
              className="ml-1 text-violet-400 hover:text-white"
            >
              ×
            </button>
          </span>
        )}

        {hasAnyFilter && (
          <button
            type="button"
            onClick={clearFilters}
            className="px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200 transition-colors min-h-[48px]"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Event list */}
      <div
        className="flex-1 overflow-y-auto"
        aria-live="polite"
        aria-atomic="false"
        aria-relevant="additions"
        aria-label="Live event stream"
      >
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-zinc-500 text-sm">
            {hasAnyFilter
              ? 'No events match the current filters.'
              : 'No orchestrator activity. Trigger a run to see events here.'}
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800/50">
            {filteredEvents.map((event) => (
              <EventRow
                key={event._id}
                event={event}
                onJobIdClick={(jobId) => navigate(`/jobs?jobId=${encodeURIComponent(jobId)}`)}
                onProfileIdClick={(profileId) => setFilterProfileId(profileId)}
                formatTimestamp={formatTimestamp}
                truncateId={truncateId}
              />
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Event row
// ---------------------------------------------------------------------------

interface EventRowProps {
  event: AdminOrchestratorEvent;
  onJobIdClick: (jobId: string) => void;
  onProfileIdClick: (profileId: string) => void;
  formatTimestamp: (ts: string) => string;
  truncateId: (id: string | null) => string;
}

function EventRow({
  event,
  onJobIdClick,
  onProfileIdClick,
  formatTimestamp,
  truncateId,
}: EventRowProps) {
  return (
    <li
      className="flex items-start gap-3 px-6 py-2 hover:bg-zinc-900/60 transition-colors"
    >
      {/* Severity dot */}
      <span
        aria-label={`Severity: ${event.Severity}`}
        className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${severityDot(event.Severity)}`}
      />

      {/* Timestamp */}
      <time
        dateTime={event.Timestamp}
        className="flex-shrink-0 w-20 text-xs text-zinc-500 pt-0.5 font-mono"
      >
        {formatTimestamp(event.Timestamp)}
      </time>

      {/* EventType pill */}
      <span
        className={`flex-shrink-0 px-2 py-0.5 rounded border text-xs font-medium ${eventTypeColor(event.EventType)}`}
      >
        {event.EventType}
      </span>

      {/* Summary */}
      <span className="flex-1 text-xs text-zinc-300 min-w-0 truncate pt-0.5">
        {event.Summary}
      </span>

      {/* JobId chip */}
      {event.JobId && (
        <button
          type="button"
          onClick={() => onJobIdClick(event.JobId!)}
          title={event.JobId}
          className="flex-shrink-0 px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 text-xs font-mono hover:bg-zinc-700 hover:text-zinc-200 transition-colors min-h-[48px]"
        >
          {truncateId(event.JobId)}
        </button>
      )}

      {/* ProfileId link */}
      {event.ProfileId && (
        <button
          type="button"
          onClick={() => onProfileIdClick(event.ProfileId!)}
          title={`Filter by profileId: ${event.ProfileId}`}
          className="flex-shrink-0 px-2 py-0.5 rounded bg-zinc-800 text-violet-400 text-xs font-mono hover:bg-zinc-700 hover:text-violet-300 transition-colors min-h-[48px]"
        >
          {truncateId(event.ProfileId)}
        </button>
      )}
    </li>
  );
}
